import DateRangeIcon from "@mui/icons-material/DateRange";
import { Box, Button, CircularProgress, Pagination, Typography } from "@mui/material";
import Drawer from "@mui/material/Drawer";

import sodexoIcon from "assets/images/sodexo.png";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getAllOrderlist,
  getAllOrderlistV2,
  getCreditOrders,
  creditAccountSearch,
} from "../../../../config/Services";
import { useSnackbar } from "../hooks/SnackbarProvider";
import useGetDevice from "../hooks/useGetDevice";
import useViewportTablePageSize, { initialTablePageSize } from "../hooks/useViewportTablePageSize";
import { OrderDetailsPage } from "../orders";
import { CreditOrderDetailsPage } from "../orders/creditOrderDetails";
import Spinner from "./../../../../components/Spinner/index";
import { convertToLocalDateTime } from "./Middleware/commonUtils";
import "./OrdersTable.css";
import OrderCard from "./components/OrderCard/OrderCard";
import { setLoading, orderFilter } from "./redux/Actions/paginationActions";
import { getFromLocalStorage } from "../../../../utils/localStorageHelpers";
import { customEventTrigger } from "../../../../analytics";

import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import CustomerPaginatedTable from "./CustomerV2Preview/Component/CustomComponent/CustomerPaginatedTable";
import { format } from "date-fns";

const cashIcon =
  "https://static.vecteezy.com/system/resources/previews/005/567/661/original/rupee-icon-indian-currency-symbol-illustration-coin-symbol-free-vector.jpg";
const cardIcon = "https://pngimg.com/uploads/mastercard/mastercard_PNG24.png";
const upiIcon = "https://logodix.com/logo/1763598.png";
const splitIcon = "https://cdn.onlinewebfonts.com/svg/img_3109.png";
const pendingIcon =
  "https://img.freepik.com/premium-vector/vector-design-payment-pending-icon-style_822882-306704.jpg";

/* ── Helper: map order status → CSS class ── */
const getStatusBadgeClass = (status) => {
  switch (status) {
    case "PAID":
      return "td-status-badge td-status-badge--paid";
    case "CREATED":
      return "td-status-badge td-status-badge--created";
    case "ADJUSTED":
      return "td-status-badge td-status-badge--adjusted";
    case "PAYMENT_PENDING":
      return "td-status-badge td-status-badge--pending";
    case "CANCELLED":
      return "td-status-badge td-status-badge--cancelled";
    case "PAYMENT_CANCELLED":
      return "td-status-badge td-status-badge--payment-cancelled";
    case "PARTIALLY_PAID":
      return "td-status-badge td-status-badge--partial-paid";
    case "REFUNDED":
      return "td-status-badge td-status-badge--refunded";
    default:
      return "td-status-badge td-status-badge--default";
  }
};

/* ── Helper: map subOrderType → source display ── */
const renderSourceCell = (source) => {
  if (!source || source === "-----") {
    return <span className="td-source-pill">{source || "—"}</span>;
  }

  const upper = source?.toUpperCase();

  if (upper?.includes("ZOMATO")) {
    return (
      <div className="td-source-aggregator">
        <div className="td-source-badge td-source-badge--zomato">Z</div>
        <span className="td-source-name">Zomato</span>
      </div>
    );
  }
  if (upper?.includes("SWIGGY")) {
    return (
      <div className="td-source-aggregator">
        <div className="td-source-badge td-source-badge--swiggy">S</div>
        <span className="td-source-name">Swiggy</span>
      </div>
    );
  }

  return <span className="td-source-pill">{source}</span>;
};

/* ── Helper: channel pill ── */
const renderChannelPill = (salesChannel) => {
  switch (salesChannel) {
    case "SALES_ORDER":
      return <span className="td-channel-pill td-channel-pill--b2b">B2B</span>;
    case "SWIGGY_ORDER":
      return <span className="td-channel-pill td-channel-pill--swiggy">Swiggy</span>;
    case "ZOMATO_ORDER":
      return <span className="td-channel-pill td-channel-pill--zomato">Zomato</span>;
    case "WHATSAPP_ORDER":
      return <span className="td-channel-pill td-channel-pill--whatsapp">WhatsApp</span>;
    case "ONDC_ORDER":
      return <span className="td-channel-pill td-channel-pill--ondc">ONDC</span>;
    default:
      return <span className="td-channel-pill td-channel-pill--b2c">B2C</span>;
  }
};

const OrdersTable = ({ date, orderValue: propOrderValue, setOrderValue: propSetOrderValue }) => {
  const isMobile = useGetDevice();
  const showSnackbar = useSnackbar();
  const getOrderText = useSelector((state) => state?.pagination?.order_text);
  const sessionId = localStorage.getItem("sessionId");

  const [localOrderValue, setLocalOrderValue] = useState({
    value: "Session orders",
    label: "Session orders",
  });
  const orderValue = propOrderValue !== undefined ? propOrderValue : localOrderValue;
  const setOrderValue = propSetOrderValue !== undefined ? propSetOrderValue : setLocalOrderValue;

  const dispatch = useDispatch();
  const [dataRows, setTableRows] = useState([]);
  const [noData, setNoData] = useState(false);
  const [state, setState] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowDetails, setRowDetails] = useState(null);
  const [orderId, setOrderId] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [pageState, setPageState] = useState({
    loader: false,
    datRows: [],
    total: 0,
    page: 1,
    pageSize: isMobile ? 6 : initialTablePageSize(),
  });
  const tableRef = useViewportTablePageSize(setPageState, !isMobile);
  const retailType = localStorage.getItem("retailType");
  const branchSubType = localStorage.getItem("branchSubType");
  const [selectedOrderType, setSelectedOrderType] = useState(null);

  useEffect(() => {
    setSelectedOrderType(null);
    setPageState((old) => ({ ...old, page: 1 }));
  }, [orderValue?.value]);

  const allowCreditPayment =
    getFromLocalStorage("appConfig")?.enabledFeatures?.ALLOW_CREDIT_PAYMENT;

  let dataArr,
    dataRow = [];

  const handleRefreshOrders = () => {
    setUpdate((prev) => !prev);
  };

  useEffect(() => {
    if (location.state?.resetToSession) {
      setOrderValue({
        value: "Session orders",
        label: "Session orders",
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  let locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const { uidx } = getFromLocalStorage("retailUserDetails");

  const renderPaymentIcon = (paymentMethod) => {
    switch (paymentMethod) {
      case "CASH":
        return cashIcon;
      case "CARD":
        return cardIcon;
      case "UPI":
        return upiIcon;
      case "SODEXO":
        return sodexoIcon;
      case "SPLIT":
        return splitIcon;
      case "DEBIT_NOTE":
        return pendingIcon;
      default:
        return "";
    }
  };

  if (getOrderText?.length == 0) {
    var filterObject =
      orderValue?.value === "Session orders"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            locationId: locId,
            ...(retailType === "RESTAURANT"
              ? {
                  orderTypes: [
                    "SWIGGY_ORDER",
                    "ZOMATO_ORDER",
                    "RESTAURANT_ORDER",
                    "ONDC_ORDER",
                    "WHATSAPP_ORDER",
                  ],
                }
              : { orderTypes: ["POS_ORDER", "WHATSAPP_ORDER", "SALES_ORDER"] }),
            sessionId: sessionId,
          }
        : orderValue?.value === "All orders"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            locationId: locId,
            ...(retailType === "RESTAURANT"
              ? {
                  orderTypes: [
                    "SWIGGY_ORDER",
                    "ZOMATO_ORDER",
                    "RESTAURANT_ORDER",
                    "ONDC_ORDER",
                    "WHATSAPP_ORDER",
                  ],
                }
              : { orderTypes: ["POS_ORDER", "WHATSAPP_ORDER", "SALES_ORDER"] }),
          }
        : orderValue?.value === "Cashier orders"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            locationId: locId,
            ...(retailType === "RESTAURANT"
              ? {
                  orderTypes: [
                    "SWIGGY_ORDER",
                    "ZOMATO_ORDER",
                    "RESTAURANT_ORDER",
                    "ONDC_ORDER",
                    "WHATSAPP_ORDER",
                  ],
                }
              : { orderTypes: ["POS_ORDER", "WHATSAPP_ORDER", "SALES_ORDER"] }),
            loggedInUserId: uidx,
          }
        : orderValue?.value === "Credit orders"
        ? { page: pageState.page - 1, pageSize: pageState.pageSize, locationId: locId }
        : orderValue?.value === "Credit Orders Listing"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            orderType: retailType === "RESTAURANT" ? "RESTAURANT_ORDER" : "POS_ORDER",
            locationId: locId,
            paymentType: "POS_CREDIT_SALE",
          }
        : orderValue?.value === "App orders"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            locationId: locId,
            orderType: "B2C_ORDER",
          }
        : orderValue?.value === "Swiggy/Zomato"
        ? {
            page: pageState.page - 1,
            pageSize: pageState.pageSize,
            locationId: locId,
            orderTypes: ["SWIGGY_ORDER", "ZOMATO_ORDER", "WHATSAPP_ORDER"],
          }
        : null;

    if (date) {
      filterObject.startDate = date;
      filterObject.endDate = date;
    }
    if (isMobile) {
      if (retailType === "RESTAURANT") {
        filterObject.orderTypes = [
          "SWIGGY_ORDER",
          "ZOMATO_ORDER",
          "RESTAURANT_ORDER",
          "ONDC_ORDER",
          "WHATSAPP_ORDER",
        ];
      } else {
        filterObject.orderTypes = ["POS_ORDER", "WHATSAPP_ORDER", "SALES_ORDER"];
      }
      filterObject.subOrderType = "MPOS_ORDER";
    }
    if (selectedOrderType && filterObject) {
      filterObject.subOrderTypes = [selectedOrderType];
    }
  } else {
    var filterObject = {
      searchBox: getOrderText,
      page: pageState.page - 1,
      pageSize: pageState.pageSize,
      ...(retailType === "RESTAURANT"
        ? {
            orderTypes: [
              "SWIGGY_ORDER",
              "ZOMATO_ORDER",
              "RESTAURANT_ORDER",
              "ONDC_ORDER",
              "WHATSAPP_ORDER",
            ],
          }
        : { orderTypes: ["POS_ORDER", "WHATSAPP_ORDER", "SALES_ORDER"] }),
    };
  }

  useEffect(() => {
    if (orderValue?.value === "In-store Delivery") {
      showSnackbar("Not available, coming soon ", "info");
      return;
    }
    let cancelled = false;
    setPageState((old) => ({ ...old, loader: true }));
    setLoading(true);

    if (orderValue?.value === "Credit orders") {
      // A global search inside the credit view filters the credit accounts list
      // via the OMS v7 accounts search API; otherwise fetch the full list.
      (async () => {
        try {
          const res = await (getOrderText?.length
            ? creditAccountSearch({
                page: pageState?.page - 1,
                pageSize: pageState.pageSize,
                orgId,
                locationId: locId,
                query: getOrderText,
              })
            : getCreditOrders(filterObject));
          if (cancelled) return;

          dataArr = res?.data?.data?.accounts;
          dataRow?.push(
            dataArr?.map((row) => ({
              id: row?.accountNoteId,
              customer_name: row?.customerName,
              contact: row?.mobileNumber,
              outstanding_orders: row?.outStandingOrders,
              outstanding_amount: row?.outStandingAmount,
              invoice_aging: row?.invoiceAging,
              last_payment: row?.lastPaymentDate
                ? convertToLocalDateTime(row?.lastPaymentDate)
                : "NA",
            }))
          );
          setNoData(false);
          setLoading(false);
          setPageState((old) => ({
            ...old,
            loader: false,
            total: res?.data?.data?.totalResults || 0,
            datRows: dataRow[0] || [],
          }));
        } catch (err) {
          if (cancelled) return;
          showSnackbar(err?.response?.data?.message || err?.message, "error");
          setPageState((old) => ({ ...old, loader: false }));
        }
      })();
      return () => { cancelled = true; };
    }

    const fetchOrders = !!getOrderText.length ? getAllOrderlistV2 : getAllOrderlist;
    fetchOrders(filterObject)
      .then((responseTxt) => {
        if (cancelled) return;
        showSnackbar("Successfully fetched the orders", "success");
        dataArr = responseTxt?.data?.data?.orderResponseList;
        dataRow?.push(
          dataArr?.map((row) => ({
            date_display: format(new Date(row?.baseOrderResponse?.createdAt), "dd MMM yyyy"),
            time_display: format(new Date(row?.baseOrderResponse?.createdAt), "hh:mm aa"),
            order_id: row?.baseOrderResponse?.orderId ? row?.baseOrderResponse?.orderId : "-----",
            invoice_id: row?.baseOrderResponse?.invoiceId
              ? row?.baseOrderResponse?.invoiceId
              : "-----",
            customer_name: row?.baseOrderResponse?.customerName || "-----",
            customer_phone: row?.baseOrderResponse?.mobileNumber || "",
            tableNumber: row?.baseOrderResponse?.tableNumber || "-----",
            items: row?.baseOrderResponse?.numberOfLineItems
              ? row?.baseOrderResponse?.numberOfLineItems
              : "-----",
            amount: row?.orderBillingDetails?.grandTotal
              ? `₹ ${row?.orderBillingDetails?.grandTotal}`
              : "-----",
            status: row?.baseOrderResponse?.orderStatus
              ? row?.baseOrderResponse?.orderStatus
              : "-----",
            paymentMethod: row?.orderBillingDetails?.paymentMethod,
            source: row?.baseOrderResponse?.subOrderType
              || row?.baseOrderResponse?.salesChannel
              || row?.baseOrderResponse?.sourceApp
              || row?.baseOrderResponse?.orderType
              || "-----",
            salesChannel: row?.baseOrderResponse?.salesChannel || "",
          }))
        );
        setTableRows(dataRow[0]);
        setSelectedRow([dataRow?.[0]?.[0]?.order_id]);
        setNoData(false);
        setLoading(false);
        setPageState((old) => ({
          ...old,
          loader: false,
          datRows: dataRow[0] || [],
          total: responseTxt?.data?.data?.totalResults || 0,
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        showSnackbar(err?.response?.data?.message || err?.message, "error");
      });
    return () => { cancelled = true; };
  }, [
    pageState.page,
    pageState.pageSize,
    update,
    getOrderText,
    orderValue,
    date,
    selectedOrderType,
  ]);

  const orderDetailsPage = (id, event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setOrderId(id);
    setState(true);
    setShowOrderDetails(true);
  };

  const handlePageChange = (event, value) => {
    setPageState((old) => ({ ...old, page: value }));
  };

  // const cancelOrder = () => {
  //   showSnackbar("Order is cancelled", "error");
  // };

  useEffect(() => {
    const filteredRows = dataRows?.filter((row) => row?.order_id === selectedRow?.[0]);

    setRowDetails(filteredRows);
  }, [selectedRow]);

  const handleKeyDown = (event) => {
    if (state) return;
    if (event.key === "ArrowLeft" && event.shiftKey) {
      setPageState((old) => ({ ...old, page: Math.max(old?.page - 1, 1) }));
    } else if (event.key === "ArrowRight" && event.shiftKey) {
      setPageState((old) => ({
        ...old,
        page: Math.min(old?.page + 1, Math.ceil(old?.total / old?.pageSize)),
      }));
    } else if (event?.key === "Enter" && rowDetails) {
      orderDetailsPage(rowDetails?.[0]?.order_id, event);
    }
  };

  useEffect(() => {
    const handleKeyDownGrid = (event) => {
      if (event.altKey && event.key === "a") {
        const firstRow = document.querySelector(".datagrid-rows");
        if (firstRow) firstRow.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDownGrid);
    return () => document.removeEventListener("keydown", handleKeyDownGrid);
  }, []);

  const renderOrders = () => {
    if (pageState.loader) {
      return (
        <Box className="order-loader-container">
          <CircularProgress />
        </Box>
      );
    }
    if (!dataRows?.length || orderValue === "In-store Delivery") {
      return (
        <Box className="order-loader-container">
          <Typography variant="h6">No Orders Found</Typography>
        </Box>
      );
    }
    return (
      <Box className="order-main-container">
        <Box className="order-item-container">
          {dataRows?.map((order, index) => (
            <OrderCard key={index} order={order} orderDetailsPage={orderDetailsPage} />
          ))}
        </Box>
        <Box className="order-pagination-container">
          <Pagination
            count={Math.ceil(pageState?.total / pageState?.pageSize)}
            page={pageState?.page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            className="order-pagination"
          />
        </Box>
      </Box>
    );
  };

  const displayOdersTable = () => {
    setOrderId("");
  };
  const handleBack = () => {
    setShowOrderDetails(false);
  };

  /* ── Column definitions — Order History ── */
  const ORDER_COLUMNS = [
    {
      key: "order_id",
      label: "ORDER ID",
      width: "11fr",
      render: (row) => (
        <div className="td-order-id">
          <span>{row?.order_id}</span>
        </div>
      ),
    },
    {
      key: "date",
      label: "DATE",
      width: "13fr",
      render: (row) => (
        <>
          <div className="td-date-primary">{row?.date_display}</div>
          <div className="td-date-secondary">{row?.time_display}</div>
        </>
      ),
    },
    {
      key: "customer_name",
      label: "CUSTOMER",
      width: "15fr",
      render: (row) => (
        <>
          <div className="td-customer-name">{row?.customer_name}</div>
          {row?.customer_phone && <div className="td-customer-phone">{row?.customer_phone}</div>}
        </>
      ),
    },
    {
      key: "source",
      label: "SOURCE",
      width: "11fr",
      render: (row) => renderSourceCell(row?.source),
    },
    {
      key: "salesChannel",
      label: "TYPE",
      width: "8fr",
      render: (row) => renderChannelPill(row?.salesChannel),
    },
    {
      key: "items",
      label: "ITEMS",
      width: "8fr",
      render: (row) => (
        <span className="td-items">
          {row?.items !== "-----" ? `${row?.items} items` : row?.items}
        </span>
      ),
    },
    {
      key: "amount",
      label: "TOTAL",
      width: "9fr",
      render: (row) => <span className="td-amount">{row?.amount}</span>,
    },
    {
      key: "paymentMethod",
      label: "PAYMENT METHOD",
      width: "13fr",
      render: (row) => <span className="td-payment-method-pill">{row?.paymentMethod || "—"}</span>,
    },
    {
      key: "status",
      label: "PAYMENT STATUS",
      width: "13fr",
      render: (row) => <div className={getStatusBadgeClass(row?.status)}>{row?.status}</div>,
    },
  ];

  /* ── Column definitions — Credit Summary ── */
  const CREDIT_ORDER_COLUMNS = [
    {
      key: "customer_name",
      label: "Customer Name",
      width: "19fr",
      render: (row) => <div className="td-customer-name">{row?.customer_name}</div>,
    },
    {
      key: "contact",
      label: "Phone Number",
      width: "16fr",
      render: (row) => <span className="td-items">{row?.contact}</span>,
    },
    {
      key: "outstanding_orders",
      label: "Outstanding Orders",
      width: "16fr",
      render: (row) => <span className="td-items">{row?.outstanding_orders}</span>,
    },
    {
      key: "outstanding_amount",
      label: "Outstanding Amount",
      width: "17fr",
      render: (row) => (
        <span className="td-outstanding-amount-pill">
          {row.outstanding_amount != null ? `₹${row?.outstanding_amount}` : "—"}
        </span>
      ),
    },
    {
      key: "invoice_aging",
      label: "Invoice Aging",
      width: "14fr",
      render: (row) => <span className="td-invoice-aging-pill">{row?.invoice_aging || "—"}</span>,
    },
    {
      key: "last_payment",
      label: "Last Payment",
      width: "18fr",
      render: (row) => <span className="td-items">{row?.last_payment}</span>,
    },
  ];

  const handleCptPageChange = (newPage) => {
    setPageState((old) => ({ ...old, page: newPage + 1 }));
  };

  const handleTableRowClick = (row) => {
    if (orderValue?.value === "Credit orders") {
      const accountNoteId = row?.id || "";
      navigate(`/products/order/customer-credit-summary/${accountNoteId}`);
    } else {
      const rowId = row?.order_id || row?.id;
      navigate(`/products/order/${rowId}`);
    }
  };

  const renderContent = () => (
    <>
      {showOrderDetails ? (
        <div className="order-details-page">
          {orderValue?.value === "Credit orders" ? (
            <CreditOrderDetailsPage orderid={orderId} onRefresh={handleRefreshOrders} />
          ) : (
            <></>
          )}
        </div>
      ) : isMobile ? (
        renderOrders()
      ) : (
        <div>
          {noData ? (
            <div className="customer-no-data-box">
              <h3>No Orders Found</h3>
            </div>
          ) : (
            <div>
              {pageState?.loader ? (
                <div className="order-loader-container">
                  <Spinner />
                </div>
              ) : (
                <div>
                  {/* ── Filter bar ── */}
                  <div className="order-type-filter-container">
                    {retailType === "RESTAURANT" && (
                      <div className="order-type-filter-tabs">
                        {[
                          { label: "All", value: null },
                          { label: "Delivery", value: "DELIVERY" },
                          { label: "Take Away", value: "TAKE_AWAY" },
                          {
                            label: "Dine In",
                            value: branchSubType === "QSR" ? "QSR_DINE_IN" : "DINE_IN",
                          },
                        ]?.map((tab) => (
                          <button
                            key={tab?.label}
                            className={`order-type-tab${
                              // selectedOrderType === tab.value ? " order-type-tab-active" : ""
                              selectedOrderType === tab?.value &&
                              orderValue?.value !== "Credit orders"
                                ? " order-type-tab-active"
                                : ""
                            }`}
                            onClick={() => {
                              if (orderValue?.value === "Credit orders") {
                                setOrderValue({ value: "All orders", label: "All orders" });
                              }
                              setSelectedOrderType(tab?.value);
                              setPageState((old) => ({ ...old, page: 1 }));
                            }}
                          >
                            {tab?.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {retailType === "RESTAURANT" && allowCreditPayment && (
                      <div
                        className="order-type-filter-divider"
                        data-testid="divider-history-secondary-tabs"
                      />
                    )}

                    {allowCreditPayment && (
                      <button
                        className={`customer-credit-summary-btn${
                          orderValue?.value === "Credit orders"
                            ? " customer-credit-summary-btn--active"
                            : ""
                        }`}
                        data-testid="button-history-credit-summary"
                        onClick={() => {
                          setOrderValue({ value: "Credit orders", label: "Credit orders" });
                        }}
                      >
                        <span>Customer Credit Summary</span>
                        {orderValue?.value === "Credit orders" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="credit-summary-cross-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOrderValue({ value: "All orders", label: "All orders" });
                            }}
                          >
                            <path d="M18 6 6 18"></path>
                            <path d="m6 6 12 12"></path>
                          </svg>
                        )}
                      </button>
                    )}
                  </div>

                  {/* ── Table ── */}
                  <div ref={tableRef} onKeyDown={handleKeyDown} tabIndex={-1} className="custom-table-container orders-responsive-table">
                    <CustomerPaginatedTable
                      columns={
                        orderValue?.value === "Credit orders" ? CREDIT_ORDER_COLUMNS : ORDER_COLUMNS
                      }
                      rows={pageState?.datRows}
                      loading={pageState?.loader}
                      meta={{
                        page: pageState?.page - 1,
                        pageSize: pageState?.pageSize,
                        totalResults: pageState?.total,
                      }}
                      onPageChange={handleCptPageChange}
                      emptyText={
                        orderValue?.value === "Credit orders"
                          ? "No Credit Orders Found"
                          : "No Orders Found"
                      }
                      rowKey={orderValue?.value === "Credit orders" ? "id" : "order_id"}
                      showPagination={true}
                      onRowClick={handleTableRowClick}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (propOrderValue === undefined) {
    return (
      <>
        <div className="orders-table-page-standalone">{renderContent()}</div>
      </>
    );
  }

  return renderContent();
};

export default OrdersTable;
